import {ICandidateApiFilterOptions, ICandidateApiSortingOptions} from "api/candidateApi/form";
import {TFilterCompany} from "api/companyApi/types";
import {TCandidateCompanyEventItem} from "mapx-components/Filters/DealExperienceSearchFilter/types";
import {removeDuplicatesFromArray} from "helpers/deduplicate";

export {
	removeDuplicatesFromArray,
	removeDuplicateObjectFromArray,
	removeDuplicateObjectFromArrayByKeepingTheFirstIndex,
} from "helpers/deduplicate";

export function isEmptyObject(obj: Record<string | number, unknown>): boolean {
	return Object.keys(obj).length === 0;
}

interface DataSet {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

function mergeSearchedCompanyWithTargetListCompaniesForAPI<T>(
	key: keyof DataSet,
	value: T[],
	dataSet: DataSet,
): T[] | null {
	let relatedValue = [];

	switch (key) {
		case "companies":
			relatedValue = dataSet.tl_companies;
			break;
		case "current_companies":
			relatedValue = dataSet.current_tl_companies;
			break;
		case "previous_companies":
			relatedValue = dataSet.previous_tl_companies;
			break;
		default:
	}

	if (Array.isArray(value) && Array.isArray(relatedValue)) {
		value = [...value, ...relatedValue];
	}

	return value;
}

export const buildQueryParameter = (dataSet: DataSet, prefix = "?"): string | null => {
	const query = Object.entries(dataSet).reduce((acc, [key, value]) => {
		if (
			key.includes("tl_companies") ||
			(Array.isArray(value) && value.length === 0) ||
			value == null
		) {
			return acc;
		}

		if (["companies", "current_companies", "previous_companies"].includes(key)) {
			const mergedValues = mergeSearchedCompanyWithTargetListCompaniesForAPI(
				key,
				value,
				dataSet,
			) as DataSet;

			value = removeDuplicatesFromArray(
				mergedValues.map((item: {id: number | string}) => item.id || item),
			).join(",");
		}

		return acc + `${key}=${Array.isArray(value) ? value.join(",") : value}&`;
	}, "");

	return query ? `${prefix}${query.slice(0, -1)}` : null;
};

export const buildCompanyFilterApiPayload = (
	dataSet: DataSet,
): {filters: TFilterCompany} | null => {
	const filters: TFilterCompany = {};

	for (const key in dataSet) {
		let value = dataSet[key];

		if (
			key === "tl_companies" ||
			key === "current_tl_companies" ||
			key === "previous_tl_companies"
		) {
			continue;
		}

		if (key === "companies" || key === "current_companies" || key === "previous_companies") {
			value = mergeSearchedCompanyWithTargetListCompaniesForAPI(key, value, dataSet);

			if (Array.isArray(value) && value.length > 0) {
				value = removeDuplicatesFromArray(
					value.map((item) => {
						const {id} = item as unknown as {id: number};

						// if item is not an object
						if (id === undefined) {
							return item;
						}

						return id;
					}),
				);

				filters[key] = value;
			}
		} else if (Array.isArray(value) && value.length > 0) {
			filters[key] = value;
		} else if (
			value !== null &&
			(typeof value === "number" ||
				key === "gender_diversity_importance" ||
				key === "ethnic_diversity_importance")
		) {
			filters[key] = value;
		} else if (
			value &&
			(key === "max_revenue" ||
				key === "min_revenue" ||
				key === "max_size" ||
				key === "min_size" ||
				key === "company_search_radius" ||
				key === "company_max_size" ||
				key === "company_min_size" ||
				key === "current_company_max_size" ||
				key === "current_company_min_size" ||
				key === "previous_company_max_size" ||
				key === "previous_company_min_size")
		) {
			filters[key] = value;
		}
	}

	if (Object.keys(filters).length > 0) {
		return {filters};
	} else {
		return null;
	}
};

export const buildCandidateApiRequestPayload = (
	dataSet: DataSet,
	recommendedSort = false,
): {filters: ICandidateApiFilterOptions; sorting_options: ICandidateApiSortingOptions} | null => {
	let filters: Record<string | number, unknown> = {};

	let sortingFilters: Record<string | number, unknown> = {};

	for (const key in dataSet) {
		let value = dataSet[key];

		if (
			key === "tl_companies" ||
			key === "current_tl_companies" ||
			key === "previous_tl_companies"
		) {
			continue;
		}

		if (key === "companies" || key === "current_companies" || key === "previous_companies") {
			value = mergeSearchedCompanyWithTargetListCompaniesForAPI(key, value, dataSet);
		}

		if (Array.isArray(value) && value.length > 0) {
			value = value
				? removeDuplicatesFromArray(
						value.map((item) => {
							if (item === null && key === "progress_status_ids") {
								return null;
							}

							const {id} = item as unknown as {id: number};

							// if item is not an object
							if (id === undefined) {
								return item;
							}

							return id;
						}),
				  )
				: value;

			filters = {...filters, [key]: value};
		} else if (
			typeof value === "string" &&
			(key === "gender_diversity_importance" ||
				key === "ethnic_diversity_importance" ||
				(recommendedSort && key === "ordering"))
		) {
			sortingFilters = {...sortingFilters, [key]: value};
		} else if (
			typeof value === "number" &&
			(key === "max_revenue" ||
				key === "min_revenue" ||
				key === "max_size" ||
				key === "min_size" ||
				key === "company_max_size" ||
				key === "company_min_size" ||
				key === "current_company_max_size" ||
				key === "current_company_min_size" ||
				key === "previous_company_max_size" ||
				key === "previous_company_max_size" ||
				key === "company_events")
		) {
			filters = {...filters, [key]: value};
		} else if (key === "full_name" && typeof value === "string" && value.trim()) {
			filters = {...filters, [key]: value.trim()};
		} else if (value && (key === "company_hierarchy_filter_mode" || key === "fit_to_brief")) {
			filters = {...filters, [key]: value};
		} else if (key === "company_events") {
			const {types, size, dates} = value as TCandidateCompanyEventItem;

			const filteredCompanyEvents: Partial<TCandidateCompanyEventItem> = {};

			if (types?.length > 0) {
				filteredCompanyEvents.types = types;
			}

			if (size?.length > 0) {
				filteredCompanyEvents.size = size;
			}

			if (dates?.length > 0) {
				filteredCompanyEvents.dates = dates;
			}

			if (Object.keys(filteredCompanyEvents).length > 0) {
				filters = {...filters, [key]: filteredCompanyEvents};
			}
		}
	}

	if (isEmptyObject(filters) && isEmptyObject(sortingFilters)) {
		return null;
	} else {
		return {
			filters,
			sorting_options: sortingFilters,
		};
	}
};

export const cleanPayloadObject = <T extends Record<string, unknown>>(payload: T): Partial<T> => {
	const cleanedPayload: Partial<T> = {};

	(Object.keys(payload) as Array<keyof T>).forEach((key) => {
		const value = payload[key];

		if (Array.isArray(value)) {
			if (value.length > 0) {
				cleanedPayload[key] = value as T[keyof T];
			}
		} else if (value !== null && value !== undefined && value !== "") {
			cleanedPayload[key] = value as T[keyof T];
		}
	});

	return cleanedPayload;
};
